<template>
    <div class="c2c-pdf-page" :style="pageStyle" :aria-label="`Page ${number}`">
        <canvas ref="canvas" aria-hidden="true"></canvas>
        <div ref="text" class="textLayer"></div>
        <div ref="annotations" class="annotationLayer" @input="fieldChanged" @change="fieldChanged"></div>
        <div v-if="error" class="p-4 text-red-700" role="alert">{{ error }}</div>
    </div>
</template>
<script>
import { syncTextAppearances, formAnnotations } from './pdf-runtime';
export default {
    props: ['pdf', 'runtime', 'number', 'scale', 'editing', 'scroller', 'fieldObjects', 'size'],
    data() { return { width: this.size?.width || 612, height: this.size?.height || 792, unit: this.size?.unit || 1, visible: false, error: '' }; },
    computed: {
        pageStyle() { return { width: `${this.width * this.scale}px`, height: `${this.height * this.scale}px`, '--scale-factor': this.scale, '--total-scale-factor': this.scale * this.unit, '--scale-round-x': '1px', '--scale-round-y': '1px' }; },
    },
    watch: { scale() { this.render(); }, editing() { this.render(); } },
    mounted() {
        this.generation = 0;
        this.observer = new IntersectionObserver(entries => {
            this.visible = entries[0].isIntersecting;
            if (this.visible) this.render();
            else this.release();
        }, { root: this.scroller, rootMargin: '800px 0px' });
        this.observer.observe(this.$el);
    },
    beforeDestroy() { this.observer.disconnect(); this.release(); },
    methods: {
        fieldChanged() {
            syncTextAppearances(this.pdf, this.fieldObjects);
            this.$emit('changed');
        },
        release() {
            this.generation++;
            if (this.task) this.task.cancel();
            if (this.textLayer) this.textLayer.cancel();
            this.task = null;
            this.$refs.canvas.width = this.$refs.canvas.height = 0;
            this.$refs.text.replaceChildren();
            this.$refs.annotations.replaceChildren();
        },
        async render() {
            const previous = this.task;
            this.release();
            if (!this.visible) return;
            const generation = this.generation;
            try {
                if (previous) await previous.promise.catch(() => {});
                const page = await this.pdf.getPage(this.number);
                if (generation !== this.generation) return;
                const normal = page.getViewport({ scale: 1 });
                this.width = normal.width; this.height = normal.height; this.unit = page.userUnit;
                this.$emit('measured', { number: this.number, width: this.width, height: this.height, unit: this.unit });
                const viewport = page.getViewport({ scale: this.scale });
                const ratio = Math.min(window.devicePixelRatio || 1, 2, Math.sqrt(16000000 / (viewport.width * viewport.height)));
                const canvas = this.$refs.canvas;
                canvas.width = Math.floor(viewport.width * ratio); canvas.height = Math.floor(viewport.height * ratio);
                canvas.style.width = `${viewport.width}px`; canvas.style.height = `${viewport.height}px`;
                this.task = page.render({ canvasContext: canvas.getContext('2d'), viewport,
                    transform: [ratio, 0, 0, ratio, 0, 0],
                    annotationMode: this.editing ? this.runtime.AnnotationMode.ENABLE_FORMS : this.runtime.AnnotationMode.ENABLE_STORAGE });
                await this.task.promise;
                if (generation !== this.generation) return;
                this.textLayer = new this.runtime.TextLayer({ container: this.$refs.text, viewport, textContentSource: page.streamTextContent() });
                await this.textLayer.render();
                if (!this.editing || generation !== this.generation) return;
                const annotations = formAnnotations(this.pdf, await page.getAnnotations());
                if (generation !== this.generation) return;
                const layer = new this.runtime.AnnotationLayer({ div: this.$refs.annotations, page,
                    viewport: viewport.clone({ dontFlip: true }), annotationStorage: this.pdf.annotationStorage,
                    linkService: { eventBus: { dispatch() {} } } });
                await layer.render({ annotations, renderForms: true, enableScripting: false, hasJSActions: false,
                    annotationStorage: this.pdf.annotationStorage, fieldObjects: this.fieldObjects,
                    linkService: { eventBus: { dispatch() {} } } });
            } catch (error) {
                if (generation === this.generation && error.name !== 'RenderingCancelledException') {
                    this.error = 'This page could not be rendered.';
                }
            }
        },
    },
};
</script>
<style>
.c2c-pdf-page { position: relative; flex: none; margin: 16px auto; background: white; box-shadow: 0 2px 8px #0003; }
.c2c-pdf-page .annotationLayer { position: absolute; inset: 0; }
.c2c-pdf-page .annotationLayer input, .c2c-pdf-page .annotationLayer textarea, .c2c-pdf-page .annotationLayer select { box-sizing: border-box; }
</style>
